// import BreadCrumbs from "../reuseable/breadcrumbs";

import { useStateValue } from "../../context/StateProvider";
import {
  formatCurrency,
  formatNumber,
  getStatusColor,
  newmessagesuccess,
  notifyOnDealSubmit,
  notifyDealMilestone,
  notifyAccountantsOnApproval,
} from "../../utils/helper";
import { deleteObject, ref } from "firebase/storage";
import PageHeader from "../PageHeader";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";

import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { useDroppable } from "@dnd-kit/core";
import { doc, getDoc } from "firebase/firestore";
import { firestored, storage } from "../../firebase/firebaseConfig";

import {
  getAlldealforAdmin,
  getTaskTemplate,
  saveitemwithid,
  setDealArchived,
  updateDealStageInFirebase,
  updateItem,
  uploadImagesToFirebase,
} from "../../utils/firebasefunctions";
import { expandStageItems } from "../../utils/taskSections";
import {
  groupPreMarketByYear,
  formatBoardDate,
} from "../../utils/preMarketGrouping";
import { retractFollowUpPlan } from "../../utils/followUpPlansStore";
import { creditedAgentId, dealCreditsAgent } from "../../utils/dealCredit";
import { excludeTestAgents } from "../../utils/testAgents";
import userlogo from "../../images/user.png";
import AddNewDeal from "../home/add-deal";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { actionType } from "../../context/reducer";
import DeleteDeal from "../deals/delete-deal";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2,
} from "lucide-react";
import { useRef } from "react";
import { useDealClientNames } from "../../hooks/useDealClientNames";
import { usePersistentAgentFilter } from "../../hooks/usePersistentAgentFilter";

// agentData is an id string on current deals and the whole agent object on
// some older ones, so every id read goes through here before it is compared.
const idOf = (value) => {
  if (value && typeof value === "object") return value.id ? String(value.id) : "";
  return value || value === 0 ? String(value) : "";
};

// Is this person ON this deal? The board is an OPERATIONAL work board, not a
// report, so this is deliberately WIDER than the credit rule in
// utils/dealCredit.js. Credit picks exactly ONE owner per deal for units and
// volume, which is right for the leaderboard and the Status Sheet but too
// narrow here: on an in-house referral the credit moves to the team lead, and
// the agent still working that file would watch it vanish off his own board.
//
// So a person is on a deal three ways, any one of which is enough:
//   1. he is the agent named on it,
//   2. he created it, or
//   3. he is the credited owner (dealCreditsAgent) - which covers both the
//      in-house referral that moved the unit to the team lead AND the old
//      "no agent named, so it falls to the team lead" case.
//
// Clause 3 is the one that was missing. Its old form only fired when the deal
// named NO agent, so on the live 1888 Walnut buyer deal - Joe as agent, Henry
// as team lead of record, unit credited to Henry - Henry only kept the card
// because he happened to have created it. Had Joe entered that deal, Henry
// filtering his own board to himself would not have seen the very deal every
// report credits to him.
export const isOnDeal = (deal, agentId) => {
  const wanted = idOf(agentId);
  if (!wanted) return false;
  if (idOf(deal?.agentData) === wanted) return true;
  if (idOf(deal?.createdby) === wanted) return true;
  return dealCreditsAgent(deal, wanted);
};

function MainDealBoard() {
  const [{ deals: allDeals, users, employees, steps, user }, dispatch] =
    useStateValue();
  const navigate = useNavigate();
  // Show every deal on the board (seller and buyer). Buyer-side deals enter
  // the pipeline at Under Contract and run through Pending / Closed just
  // like seller deals — the working stages where an agent needs visibility.
  // The early columns (Prospect / CMAs / Listing Appointment / Pre-Market /
  // Coming Soon / Active) are seller-only by nature because buyer deals
  // don't exist there, so no extra filtering is needed.
  const deals = allDeals || [];
  // Resolves each deal's `customers` to display names. Handles both the modern
  // contact-ID shape (["contactId", ...]) and the legacy inline-object shape,
  // and filters out names that don't resolve so cards never show an orphan "&".
  const { getClientName } = useDealClientNames(deals);
  const filters = ["All", "Closing Soon", "New This Week"];
  const [activeFilter, setActiveFilter] = useState("All");
  const [showArchived, setShowArchived] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    if (term.length < 2) return;
    const timer = setTimeout(() => {
      const matches = (deals || []).filter((d) =>
        (d.propertyaddress || "").toLowerCase().includes(term),
      );
      if (matches.length === 1) {
        const target = matches[0];
        setSearchTerm("");
        navigate(`/dashboard/deals-detail/${target.createdat}?page=deal-board`);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, deals, navigate]);

  const [activeCard, setActiveCard] = useState(null);
  // Pre-Market year-section open/closed overrides (key -> bool). Falls back to
  // each section's defaultOpen when the user hasn't toggled it.
  const [pmOpen, setPmOpen] = useState({});
  const [isStatus, setIsStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editdata, setEditData] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [tasks, setTasks] = useState({});
  const currentYear = new Date().getFullYear();

  // Archived deals are hidden from the board unless the user toggles them on.
  // Admin / Supervisor / Accountant see all deals.
  // Employees see:
  //   - every listing in Active or Coming Soon (team-wide visibility so
  //     agents can refer clients to active listings)
  //   - their own deals in every other stage (where they're the creator
  //     or the agent on the deal)
  const isPrivileged =
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    user?.role === "Accountant";
  // "Viewing: <agent>" filter for privileged roles — defaults to All Agents
  // (the board is the team's, not "mine") and persists per user across
  // visits, same mechanism the pipelines use for their agent filter.
  const [viewAgent, setViewAgent] = usePersistentAgentFilter(
    user?.id,
    "deals-board",
    "all",
  );
  // Agents for the dropdown, with the current user guaranteed to be present
  // (and listed first) so picking themselves always has a matching option.
  const agentOptions = useMemo(() => {
    const list = excludeTestAgents(employees || []);
    if (user?.id && !list.some((e) => e.id === user.id)) {
      return [
        {
          id: user.id,
          firstname: user.firstname,
          lastname: user.lastname,
          email: user.email,
        },
        ...list,
      ];
    }
    return list;
  }, [employees, user]);
  const TEAM_WIDE_STAGES = new Set(["active", "coming soon"]);
  const visibleDeals = (deals || []).filter((d) => {
    if (!showArchived && d?.archived) return false;
    // File-requests parked in CMAs stay off the board until the lead signs —
    // pre-signing work lives in the pipeline now. Checked before the
    // privileged short-circuit so it's hidden for every role.
    if (d?.requestType === "fileRequest" && d?.dealStage === "CMAs")
      return false;
    // Both branches below settle "whose deal is this?" with the SAME isOnDeal
    // helper and nothing else, so a Team Lead filtering to an agent can never
    // drift from what that agent sees on his own board.
    if (isPrivileged) {
      if (viewAgent === "all") return true;
      return isOnDeal(d, viewAgent);
    }
    const stage = (d?.dealStage || "").toLowerCase();
    if (TEAM_WIDE_STAGES.has(stage)) return true;
    return isOnDeal(d, user?.id);
  });

  const canManageDeal = (deal) =>
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    (!!user?.id && !!deal?.createdby && user.id === deal.createdby);

  const handleArchiveDeal = async (deal) => {
    const next = !deal?.archived;
    try {
      await setDealArchived(deal.createdat, next);
      toast.success(next ? "Card archived" : "Card restored");
      await getalldeal();
    } catch (err) {
      console.error("Archive failed:", err);
      toast.error("Failed to update card");
    }
  };

  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const handleDeleteClick = (deal) => setDeleteCandidate(deal);

  const scrollRef = useRef(null);

  // NOTE: "Prospect" and "Listing Appointment" no longer have columns on the
  // board (pre-signing work lives in the pipeline now). They're intentionally
  // kept in beforeActiveStages / columnIds / the stageOrder below so the
  // canonical stage order and the drag-to-active logic stay correct for any
  // legacy deal still carrying those stages — there's just no droppable column
  // rendered for them.
  const beforeActiveStages = [
    "Prospect",
    "On Hold",
    "CMAs",
    "Listing Appointment",
    "Pre-Market",
    "Coming Soon",
    "Active",
  ];

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const onHoldDeals = visibleDeals?.filter((deal) => {
    const isOnHold = deal?.dealStage === "On Hold";

    // const employeeCheck =
    //   user.role === "Employee"
    //     ? deal?.agentData === user.id || deal?.createdby === user.id
    //     : true;

    return isOnHold;
  });

  const CMAsDeals = visibleDeals?.filter((deal) => {
    const isCMAs = deal?.dealStage === "CMAs";

    // const employeeCheck =
    //   user.role === "Employee"
    //     ? deal?.agentData === user.id || deal?.createdby === user.id
    //     : true;

    return isCMAs;
  });

  const preMarketDeals = visibleDeals?.filter((deal) => {
    const isPreMarket = deal?.dealStage === "Pre-Market";

    // const employeeCheck =
    //   user.role === "Employee"
    //     ? deal?.agentData === user.id || deal?.createdby === user.id
    //     : true;

    return isPreMarket;
  });

  const comingSoonDeals = visibleDeals?.filter((deal) => {
    const isComingSoon = deal?.dealStage === "Coming Soon";

    // const employeeCheck =
    //   user.role === "Employee"
    //     ? deal?.agentData === user.id || deal?.createdby === user.id
    //     : true;

    return isComingSoon;
  });

  const underContractDeals = visibleDeals?.filter((deal) => {
    const isUnderContract = deal?.dealStage === "Under Contract";
    if (!isUnderContract) return false;
    // See pendingDeals comment — same reasoning. Employees see every deal
    // they own in this stage so turn-ins still awaiting admin approval and
    // deals closing just past year-end remain visible.
    if (user.role === "Employee") return true;
    const isApproved = deal?.status === "Approved";
    const isCurrentYear =
      new Date(deal?.closingdate).getFullYear() === currentYear;
    return isApproved && isCurrentYear;
  });

  const pendingDeals = visibleDeals?.filter((deal) => {
    const isPending = deal?.dealStage === "Pending";
    if (!isPending) return false;

    // Agents (Employee) see every pending-stage deal they own. visibleDeals
    // already restricts them to their own work, so no further ownership
    // check is needed. We intentionally skip the status === "Approved" and
    // isCurrentYear gates here: those would hide turn-ins still awaiting
    // admin approval (deal.status === "Pending") and any pending sale whose
    // closing date is set just past year-end (or not yet set, which makes
    // getFullYear() return NaN). Both cases are deals the agent needs to see.
    if (user.role === "Employee") return true;

    // Admin / Supervisor / Accountant: keep the board focused on approved,
    // current-year pipeline. The dashboard's "Pending Approval" panel is
    // the dedicated place for triaging status === "Pending" turn-ins.
    const isApproved = deal?.status === "Approved";
    const isCurrentYear =
      new Date(deal?.closingdate).getFullYear() === currentYear;
    return isApproved && isCurrentYear;
  });

  const activeDeals = visibleDeals?.filter((deal) => {
    const isActive = deal?.dealStage === "Active";
    // const isApproved = deal?.status === "Approved";
    // const isCurrentYear =
    //   new Date(deal?.closingdate).getFullYear() === currentYear;

    // const employeeCheck =
    //   user.role === "Employee"
    //     ? deal?.agentData === user.id || deal?.createdby === user.id
    //     : true; // Non-employee bypass

    return isActive;
  });

  const closedDeals = visibleDeals?.filter((deal) => {
    const isClosed = deal?.dealStage === "Closed";
    if (!isClosed) return false;
    // Closed is the historical bucket — without a year gate it accumulates
    // every deal an agent ever closed (Scott has 38). The board is a
    // working view, not a permanent archive, so restrict to the current
    // year for everyone. (Older closed deals are still reachable from the
    // Transactions page.)
    const isCurrentYear =
      new Date(deal?.closingdate).getFullYear() === currentYear;
    if (!isCurrentYear) return false;
    // Employees: see their own closed deals from this year regardless of
    // admin approval status. Privileged roles keep the "Approved" gate so
    // the dashboard approval queue is the single place to triage pending
    // turn-ins.
    if (user.role === "Employee") return true;
    return deal?.status === "Approved";
  });

  const cancelledDeals = visibleDeals?.filter((deal) => {
    const isCancelled = deal?.dealStage === "Cancelled";
    if (!isCancelled) return false;
    // Employees: see every cancelled deal they own. See pendingDeals comment.
    if (user.role === "Employee") return true;
    const isApproved = deal?.status === "Approved";
    const isCurrentYear =
      new Date(deal?.closingdate).getFullYear() === currentYear;
    return isApproved && isCurrentYear;
  });

  const expiryDeals = visibleDeals?.filter((deal) => {
    const isExpired = deal?.dealStage === "Expired";
    if (!isExpired) return false;
    // Employees: see every expired deal they own. See pendingDeals comment.
    if (user.role === "Employee") return true;
    const isApproved = deal?.status === "Approved";
    const isCurrentYear =
      new Date(deal?.closingdate).getFullYear() === currentYear;
    return isApproved && isCurrentYear;
  });

  const applyActiveFilter = (dealsArray, stage) => {
    return dealsArray?.filter((deal) => {
      let matchActiveFilter = true;

      if (activeFilter === "Closing Soon") {
        const today = new Date();
        const oneMonthLater = new Date();
        oneMonthLater.setMonth(today.getMonth() + 1);

        const dealClosingDate = new Date(deal.closingdate);

        let employeeMatch = true;
        if (user.role === "Employee") {
          employeeMatch = deal.agentData === user.id;
        }

        matchActiveFilter =
          dealClosingDate >= today &&
          dealClosingDate <= oneMonthLater &&
          employeeMatch;
      } else if (activeFilter === "New This Week") {
        const today = new Date();
        const firstDayOfWeek = new Date(
          today.setDate(today.getDate() - today.getDay() + 1),
        ); // Monday
        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6); // Sunday

        const dealCreatedDate = new Date(deal.createdat);

        let employeeMatch = true;
        if (user.role === "Employee") {
          employeeMatch = deal.agentData === user.id;
        }

        matchActiveFilter =
          dealCreatedDate >= firstDayOfWeek &&
          dealCreatedDate <= lastDayOfWeek &&
          employeeMatch;
      } else if (activeFilter === "Buyer") {
        matchActiveFilter = deal.dealtype === "Buyer";
      } else if (activeFilter === "Seller") {
        matchActiveFilter = deal.dealtype === "Seller";
      }

      return matchActiveFilter;
    });
  };

  const filterBySearch = (dealsArray) => {
    if (!searchTerm.trim()) return dealsArray; // no search, return all

    return dealsArray.filter((deal) => {
      // You can customize fields to search
      const address = deal.propertyaddress?.toLowerCase() || "";

      return address.includes(searchTerm.toLowerCase());
    });
  };

  const filteredUnderContractDeals = filterBySearch(
    applyActiveFilter(underContractDeals),
  );
  const filteredOnHoldDeals = filterBySearch(applyActiveFilter(onHoldDeals));
  const filteredCMAsDeals = filterBySearch(applyActiveFilter(CMAsDeals));
  const filteredpreMarketDeals = filterBySearch(
    applyActiveFilter(preMarketDeals),
  );
  const preMarketSections = groupPreMarketByYear(
    filteredpreMarketDeals,
    currentYear,
  );
  const filteredComingSoonDeals = filterBySearch(
    applyActiveFilter(comingSoonDeals),
  );
  const filteredPendingDeals = filterBySearch(applyActiveFilter(pendingDeals));
  const filteredActiveDeals = filterBySearch(applyActiveFilter(activeDeals));
  const filteredClosedDeals = filterBySearch(applyActiveFilter(closedDeals));
  const filteredCancelledDeals = filterBySearch(
    applyActiveFilter(cancelledDeals),
  );
  const filteredExpiryDeals = filterBySearch(applyActiveFilter(expiryDeals));

  const getInitials = (id) => {
    if (!id) return "NA";

    const user = users.find((x) => x.id === id);

    if (!user) return "NA";

    const fullName = `${user.firstname} ${user.lastname}`;

    const parts = fullName.trim().split(" ").filter(Boolean);

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return (parts[0][0] + parts[1][0]).toUpperCase();
  };



  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Distance-based activation: a click is "press and release without moving".
      // Drag only kicks in after the pointer travels 8px from its start.
      // This makes opening a card much more forgiving — no time limit on hold.
      activationConstraint: { distance: 8 },
    }),
  );

  const handleDragStart = (event) => {
    const { active } = event;
    const draggedDeal = deals.find((d) => String(d.createdat) === active.id);
    setActiveCard(draggedDeal);
  };

  const columnIds = [
    "Prospect",
    "On Hold",
    "CMAs",
    "Listing Appointment",
    "Pre-Market",
    "Coming Soon",
    "Active",
    "Under Contract",
    "Pending",
    "Closed",
    "Cancelled",
    "Expired",
  ];

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeIndex = deals.findIndex(
      (d) => String(d.createdat) === active.id,
    );
    const movedItem = deals[activeIndex];

    let newStage = movedItem.dealStage;
    let targetIndex = activeIndex;

    if (columnIds.includes(over.id)) {
      newStage = over.id;
      targetIndex = visibleDeals.filter((d) => d.dealStage === newStage).length;
    } else {
      const overDeal = deals.find((d) => String(d.createdat) === over.id);
      console.log("over.id", over.id);
      console.log(overDeal);
      newStage = overDeal.dealStage;
      targetIndex = deals.findIndex((d) => d.createdat === overDeal.createdat);
    }

    const isBeforeActive = beforeActiveStages.includes(movedItem.dealStage);
    const isMovingToActiveOrAfter =
      columnIds.indexOf(newStage) >= columnIds.indexOf("Under Contract");

    if (isBeforeActive && isMovingToActiveOrAfter && !movedItem.closingdate) {
      // 🔥 popup open
      setEditData({ ...movedItem, dealStage: newStage });
      setIsEditOpen(true);

      // 🔥 store move
      setPendingMove({
        item: movedItem,
        newStage,
        targetIndex,
        oldStage: movedItem.dealStage,
      });

      toast.info(
        `Please add deal details before transferring deal to ${newStage}`,
      );
      return; // ❌ stop normal flow
    }

    // let updatedDeals = [...deals];
    // updatedDeals.splice(activeIndex, 1);
    // updatedDeals.splice(targetIndex, 0, { ...movedItem, dealStage: newStage });
    let updatedDeals = [...deals];
    const oldTasks = structuredClone(movedItem.tasks || []);

    const resulttask = syncDealTasksWithStatus(movedItem, newStage);

    const getTaskId = (t) => t.id ?? t.createdat ?? t.title;

    // BEFORE state
    const beforeTaskIds = new Set(oldTasks.map(getTaskId));

    // AFTER state (sync result)
    const afterTasks = resulttask.tasks || [];

    // REAL diff
    const newlyAddedTasks = afterTasks.filter((t) => {
      return !beforeTaskIds.has(getTaskId(t));
    });

    console.log("newlyAddedTasks", newlyAddedTasks);

    const updatedMovedItem = {
      ...movedItem,
      dealStage: newStage,
      tasks: resulttask.tasks,
      taskStatusTracker: resulttask.taskStatusTracker,
    };

    updatedDeals.splice(activeIndex, 1);
    updatedDeals.splice(targetIndex, 0, updatedMovedItem);

    // updatedDeals is derived from `deals`, which now equals allDeals
    // (buyer deals included), so no separate re-merge is needed.
    dispatch({
      type: actionType.STORE_DEALS,
      deals: updatedDeals,
    });
    // await updateDealStageInFirebase(String(movedItem.createdat), newStage);
    await updateDealStageInFirebase(String(movedItem.createdat), newStage, {
      tasks: updatedMovedItem.tasks,
      taskStatusTracker: updatedMovedItem.taskStatusTracker,
    });
    // Push a milestone notification only when the stage actually changed.
    if (newStage !== movedItem.dealStage) {
      notifyDealMilestone(updatedMovedItem, newStage, users, user).catch(
        () => {},
      );
      // A deal dragged back out of Closed retracts its follow-up plan
      // immediately, same as the stage-pill hook — the notify sweep's retract
      // pass only catches this on the owner's NEXT login otherwise.
      if (movedItem.dealStage === "Closed" && newStage !== "Closed") {
        retractFollowUpPlan(String(movedItem.createdat)).catch((e) =>
          console.error("Follow-up plan retract failed:", e),
        );
      }
    }
    if (newlyAddedTasks.length > 0) {
      newlyAddedTasks.forEach((task) => {
        if (task?.assignee) {
          newmessagesuccess(
            "New Task Assigned",
            task.assignee?.id,
            task?.title || "",
            task.id || "",
            "newtask",
            movedItem.createdat,
          );
        }
      });
    }
  };

  // Tap-to-move from a card's stage menu: reuse the exact drag-end path by
  // synthesizing a drop of this deal onto the target stage's column, so tap and
  // drag stay 100% identical (task sync, the go-Active gate, Firestore, alerts).
  const moveDealToStage = (deal, newStage) => {
    if (!deal || !newStage || newStage === deal.dealStage) return;
    return handleDragEnd({
      active: { id: String(deal.createdat) },
      over: { id: newStage },
    });
  };

  const submitdeal = async (data, bool) => {
    let id = Date.now();
    setIsLoading(true);
    try {
      let imageUrls = [];

      if (data.step1.propertyphotofiles?.length > 0) {
        imageUrls = await uploadImagesToFirebase(data.step1.propertyphotofiles);
      }

      const resulttask = syncDealTasksWithStatus(
        {
          dealStage: "Templates", // 👈 force start from beginning
          tasks: [],
          taskStatusTracker: {},
        },
        data.step1.dealStage,
      );

      let newdata = {
        status: bool ? "Approved" : "Pending",
        requestType: "saleTurnIn",
        propertyphoto: imageUrls,
        dealtype: data.step2.clientType,
        customers: data.step2.clientinfo,
        // priority: data.step1.Priority,
        dealStage: data.step1.dealStage,
        financingType: data.step1.financingType,
        // listingPrice: data.step1.listingPrice,
        earnestMoney: data.step1.earnestMoney,
        // mlsNumber: data.step1.mlsNumber,
        offerPrice: data.step1.offerPrice,
        propertyType: data.step1.propertyType,
        agentCommission: data.step3.agentCommission,
        fixedagentcommission: data.step3.fixedagentcommission,
        agentData: data.step3.agentid,
        agentName: data.step3.agentName,
        brokeradminfee: data.step3.brokeradminfee,
        captype: data.step3.captype,
        captypedescription: data.step3.captypedescription,
        closingdate: data.step1.closingdate,
        inspection: data.step1.inspection,
        propertyLocation: data.step1.propertyLocation,
        inspectionDeadline: data.step1.inspectionDeadline,
        financingDeadline: data.step1.financingDeadline,
        leadCommission: data.step3.leadCommission,
        leadsource: data.step3.leadsource,
        propertyaddress: data.step1.propertyaddress,
        splitdescription: data.step3.splitdescription,
        splittype: data.step3.splittype,
        inhousereferral: data.step3?.inhousereferral || false,
        teamleadName: data.step3.teamleadName,
        flatfee: data.step4.flatfee,
        flatfeeamount: data.step4.flatfeeamount,
        assignedteamlead: data.step3.assignedteamlead,
        thirdpartyagentCommission: data.step3.thirdpartyagentCommission,
        thirdpartyagentName: data.step3.thirdpartyagentName,
        agentCommissionamount: data.step4.agentCommissionamount,
        bankcommission: data.step4.bankcommission,
        buyersidecommissionrate: data.step4.buyersidecommissionrate,
        leadsourceCommissionamount: data.step4.leadsourceCommissionamount,
        sellersidecommissionrate: data.step4.sellersidecommissionrate,
        takehomecommissionamount: data.step4.takehomecommissionamount,
        teamleadCommissionamount: data.step4.teamleadCommissionamount,
        teamleaddiscount: data.step4.teamleaddiscount,
        agentdiscount: data.step4.agentdiscount,

        teamleadcommissionrate: data.step4.teamleadcommissionrate,
        thirdpartyagentcommissionamount:
          data.step4.thirdpartyagentcommissionamount,
        totalCommissionamount: data.step4.totalCommissionamount,
        totalcommissionrate: data.step4.totalcommissionrate,
        tasks: resulttask.tasks,
        taskStatusTracker: resulttask.taskStatusTracker,

        createdby: user.id,
        createdat: id,
      };

      console.log(newdata);

      let result = await saveitemwithid("deals", id.toString(), newdata);

      if (result === "success") {
        const newTasks = resulttask.tasks;

        if (newTasks.length > 0) {
          newTasks.forEach((task) => {
            if (task?.assignee) {
              newmessagesuccess(
                "New Task Assigned",
                task.assignee?.id,
                task?.title || "",
                task.id || "",
                "newtask",
                id, // 👈 yahan createdat use karo (id)
              );
            }
          });
        }
        try {
          await notifyOnDealSubmit({ ...newdata, createdat: id }, users, user);
        } catch (notifyErr) {
          console.error("Failed to notify on deal submit:", notifyErr);
        }
        toast.success("Deal created successfully!");
        getalldeal();
        setIsOpen(false);
        setIsLoading(false);
      } else {
        toast.error(result || "Failed to create deal");
        setIsLoading(false);
      }
    } catch (err) {
      toast.error(err.message);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const updatedeal = async (data, bool, id) => {
    setIsLoading(true);

    try {
      let finalImageUrls = [];
      const incomingFiles = data?.step1?.propertyphotofiles || [];

      // 🔥 1. Old data fetch
      const oldDoc = await getDoc(doc(firestored, "deals", id.toString()));
      const oldData = oldDoc.exists() ? oldDoc.data() : null;
      const oldImages = oldData?.propertyphoto || [];

      // 🔥 2. Detect new images
      const isNewImages =
        incomingFiles.length > 0 && typeof incomingFiles[0] !== "string";

      if (isNewImages) {
        // 🔥 Upload new
        finalImageUrls = await uploadImagesToFirebase(incomingFiles);

        // 🔥 Delete old
        await Promise.all(
          oldImages.map(async (url) => {
            try {
              const fileRef = ref(storage, url);
              await deleteObject(fileRef);
            } catch (err) {
              console.log("Old image delete error:", err);
            }
          }),
        );
      } else {
        // 🔥 Keep existing URLs only
        finalImageUrls = incomingFiles.filter(
          (file) => typeof file === "string",
        );
      }

      const resulttask = syncDealTasksWithStatus(
        {
          ...editdata,
          dealStage: pendingMove?.oldStage || editdata.dealStage, // ✅ FIX
        },
        data.step1.dealStage,
      );

      const oldTaskIds = new Set(
        (oldData?.tasks || []).map((t) => t.id || t.createdat),
      );

      const newlyAddedTasks = resulttask.tasks.filter((t) => {
        const taskId = t.id || t.createdat;
        return !oldTaskIds.has(taskId);
      });

      // 🔥 Prepare data
      const newdata = {
        status:
          user.role === "Admin" || user.role === "Supervisor"
            ? "Approved"
            : editdata.status,

        // Only approving saves may (re)stamp the approver — flag alerts
        // route to `approvedby`, so a plain agent edit must not steal it.
        ...(user.role === "Admin" || user.role === "Supervisor"
          ? { approvedby: user.id }
          : {}),
        propertyphoto: finalImageUrls,

        dealtype: data.step2.clientType,
        customers: data.step2.clientinfo,
        dealStage: data.step1.dealStage,
        financingType: data.step1.financingType,
        earnestMoney: data.step1.earnestMoney,
        inspection: data.step1.inspection,
        inspectionDeadline: data.step1.inspectionDeadline,
        financingDeadline: data.step1.financingDeadline,
        offerPrice: data.step1.offerPrice,
        propertyType: data.step1.propertyType,
        agentCommission: data.step3.agentCommission,
        fixedagentcommission: data.step3.fixedagentcommission,
        agentData: data.step3.agentid,
        agentName: data.step3.agentName,
        brokeradminfee: data.step3.brokeradminfee,
        captype: data.step3.captype,
        captypedescription: data.step3.captypedescription,
        closingdate: data.step1.closingdate,
        leadCommission: data.step3.leadCommission,
        leadsource: data.step3.leadsource,
        propertyaddress: data.step1.propertyaddress,
        splitdescription: data.step3.splitdescription,
        splittype: data.step3.splittype,
        inhousereferral: data.step3?.inhousereferral || false,
        teamleadName: data.step3.teamleadName,
        propertyLocation: data.step1.propertyLocation,
        flatfee: data.step4.flatfee,
        flatfeeamount: data.step4.flatfeeamount,
        assignedteamlead: data.step3.assignedteamlead,
        thirdpartyagentCommission: data.step3.thirdpartyagentCommission,
        thirdpartyagentName: data.step3.thirdpartyagentName,
        agentCommissionamount: data.step4.agentCommissionamount,
        bankcommission: data.step4.bankcommission,
        buyersidecommissionrate: data.step4.buyersidecommissionrate,
        leadsourceCommissionamount: data.step4.leadsourceCommissionamount,
        sellersidecommissionrate: data.step4.sellersidecommissionrate,
        takehomecommissionamount: data.step4.takehomecommissionamount,
        teamleadCommissionamount: data.step4.teamleadCommissionamount,
        teamleaddiscount: data.step4.teamleaddiscount,
        agentdiscount: data.step4.agentdiscount,
        teamleadcommissionrate: data.step4.teamleadcommissionrate,
        thirdpartyagentcommissionamount:
          data.step4.thirdpartyagentcommissionamount,
        totalCommissionamount: data.step4.totalCommissionamount,
        totalcommissionrate: data.step4.totalcommissionrate,
        tasks: resulttask.tasks,
        taskStatusTracker: resulttask.taskStatusTracker,
      };

      // 🔥 Update Firebase
      const result = await updateItem("deals", id, newdata);

      if (result === "success") {
        // Milestone push only when the stage actually changed (this same form
        // is also used for plain deal edits, so compare against the prior stage).
        const priorStage = (allDeals || []).find(
          (d) => d.createdat === id,
        )?.dealStage;
        if (priorStage && priorStage !== newdata.dealStage) {
          notifyDealMilestone(
            { ...newdata, createdat: id },
            newdata.dealStage,
            users,
            user,
          ).catch(() => {});
        }
        if (newlyAddedTasks.length > 0) {
          newlyAddedTasks.forEach((task) => {
            if (task?.assignee) {
              newmessagesuccess(
                "New Task Assigned",
                task.assignee?.id,
                task?.title || "",
                task.id || "",
                "newtask",
                id,
              );
            }
          });
        }
        // A board edit by an Admin/Supervisor auto-approves a pending turn-in
        // (same flip as the Transactions page) — tell the accountants here too.
        if (
          oldData?.status !== "Approved" &&
          newdata.status === "Approved" &&
          oldData?.requestType === "saleTurnIn"
        ) {
          notifyAccountantsOnApproval(
            { ...oldData, ...newdata, createdat: id },
            users,
            user,
          ).catch((e) => console.error("Accountant notify failed:", e));
        }
        toast.success("Deal updated successfully!");

        // Map over the full context array so buyer deals (hidden from the
        // board) are preserved when we dispatch back to context.
        const updatedDeals = (allDeals || []).map((item) =>
          item.createdat === id ? { ...item, ...newdata } : item,
        );

        dispatch({
          type: actionType.STORE_DEALS,
          deals: updatedDeals,
        });

        setIsEditOpen(false);
      } else {
        toast.error(result || "Failed to update deal");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const syncDealTasksWithStatus = (deal, newStage) => {
    const stageOrder = [
      "Prospect",
      "On Hold",
      "CMAs",
      "Listing Appointment",
      "Pre-Market",
      "Coming Soon",
      "Active",
      "Under Contract",
      "Pending",
      "Closed",
    ];

    const tracker = deal.taskStatusTracker || {};
    const updatedTasks = deal.tasks || [];

    const startIndex = Math.min(
      stageOrder.indexOf(deal.dealStage),
      stageOrder.indexOf(newStage),
    );

    const endIndex = Math.max(
      stageOrder.indexOf(deal.dealStage),
      stageOrder.indexOf(newStage),
    );

    const _agentId = deal.agentData;
    const _agentUser = (users || []).find((u) => u.id === _agentId);
    const _coordId = _agentUser?.listingCoordinatorId;
    const _coord = _coordId ? (users || []).find((u) => u.id === _coordId) : null;
    const coordinator = _coord
      ? { id: _coord.id, firstname: _coord.firstname, lastname: _coord.lastname, profilepicture: _coord.profilepicture || "", role: _coord.role }
      : null;
    const _txCoordId = _agentUser?.transactionCoordinatorId;
    const _txCoord = _txCoordId ? (users || []).find((u) => u.id === _txCoordId) : null;
    const transactionCoordinator = _txCoord
      ? { id: _txCoord.id, firstname: _txCoord.firstname, lastname: _txCoord.lastname, profilepicture: _txCoord.profilepicture || "", role: _txCoord.role }
      : null;

    for (let i = startIndex; i <= endIndex; i++) {
      const stage = stageOrder[i];

      // ✅ already applied → skip
      if (tracker[stage]) continue;

      const stageTasks = expandStageItems(tasks?.[stage] || [], stage, { coordinator, transactionCoordinator });

      stageTasks.forEach((t) => {
        const taskId = t.id || t.createdat;

        const exists = updatedTasks.some((x) => x.taskId === taskId);

        if (!exists) {
          updatedTasks.push({
            ...t,
          });
        }
      });

      // ✅ mark this stage as applied
      tracker[stage] = true;
    }

    return {
      tasks: updatedTasks,
      taskStatusTracker: tracker,
    };
  };

  async function getalldeal() {
    await getAlldealforAdmin("deals")
      .then(async (response) => {
        dispatch({
          type: actionType.STORE_DEALS,
          deals: response,
        });
      })
      .catch((err) => {
        toast.error(String(err?.message || err));
      });
  }

  useEffect(() => {
    const fetchData = async () => {
      const data = await getTaskTemplate();
      setTasks(data || {});
      console.log("task templates", data);
    };

    fetchData();
  }, []);

  return (
    <div className="h-screen flex flex-col">
      <PageHeader title="Deals Board" />
      <div className="flex-shrink-0 px-6 pt-3 pb-2 bg-white border-b border-black/[0.06] flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-md w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-themeColor"
        />
        <div className="flex flex-wrap items-center gap-1.5">
          {filters.map((item) => (
            <button
              key={item}
              onClick={() => setActiveFilter(item)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                activeFilter === item
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-900 hover:text-slate-900"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
        {isPrivileged && (
          <select
            value={viewAgent}
            onChange={(e) => setViewAgent(e.target.value)}
            className="bg-slate-50 border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
            aria-label="Viewing agent"
          >
            <option value="all">All Agents</option>
            {agentOptions.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {([emp.firstname, emp.lastname].filter(Boolean).join(" ") ||
                  emp.email) + (emp.id === user?.id ? " (Me)" : "")}
              </option>
            ))}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowArchived((v) => !v)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
              showArchived
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-white text-slate-600 border-slate-200 hover:border-amber-500 hover:text-amber-700"
            }`}
          >
            {showArchived ? "Hide Archived" : "Show Archived"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/dashboard/recently-deleted")}
            className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-slate-600 border-slate-200 hover:border-red-500 hover:text-red-700 transition"
          >
            Recently Deleted
          </button>
        </div>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        autoScroll={true}
      >
        <div className="relative w-full px-4 lg:px-6 pt-3 flex-1 min-h-0 flex flex-col">
          <button
            onClick={scrollLeft}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-1.5 rounded-full shadow border border-slate-200 text-slate-600"
          >
            <ChevronLeft size={16} />
          </button>

          {/* Scroll Right Button */}
          <button
            onClick={scrollRight}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white p-1.5 rounded-full shadow border border-slate-200 text-slate-600"
          >
            <ChevronRight size={16} />
          </button>
          <div
            ref={scrollRef}
            className="flex gap-3 overflow-x-auto overflow-y-hidden scroll-smooth panelScroll flex-1 min-h-0 pb-3 snap-x snap-mandatory sm:snap-none scroll-pl-3"
          >
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("On Hold")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">On Hold</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredOnHoldDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="On Hold"
                items={filteredOnHoldDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="On Hold">
                  {filteredOnHoldDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No on hold found</p>
                    </div>
                  ) : (
                    filteredOnHoldDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>
            </div>
            <div className={`w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg ${filteredCMAsDeals.length === 0 ? "hidden" : ""}`}>
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("CMAs")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">CMAs</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredCMAsDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="CMAs"
                items={filteredCMAsDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="CMAs">
                  {filteredCMAsDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No CMAs found</p>
                    </div>
                  ) : (
                    filteredCMAsDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Pre-Market")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Pre-Market</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredpreMarketDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Pre-Market"
                items={filteredpreMarketDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Pre-Market">
                  {filteredpreMarketDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No pre market found</p>
                    </div>
                  ) : (
                    preMarketSections.map((section) => {
                      const isOpen = pmOpen[section.key] ?? section.defaultOpen;
                      return (
                        <div key={section.key} className="mb-2">
                          <button
                            type="button"
                            onClick={() =>
                              setPmOpen((prev) => ({
                                ...prev,
                                [section.key]: !isOpen,
                              }))
                            }
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded bg-slate-200/70 text-xs font-semibold text-slate-600"
                          >
                            <span className="w-3 inline-block">
                              {isOpen ? "▾" : "▸"}
                            </span>
                            <span>{section.label}</span>
                            <span className="ml-auto bg-white rounded-full px-2 text-[11px] text-slate-500">
                              {section.deals.length}
                            </span>
                          </button>
                          {isOpen &&
                            section.deals.map((item, index) => (
                              <SortableCard
                                key={item.createdat}
                                item={item}
                                index={index}
                                onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                              >
                                <CommonCard
                                  item={item}
                                  users={users}
                                  getInitials={getInitials}
                                  getClientName={getClientName}
                                  canManage={canManageDeal(item)}
                                  canDelete={canManageDeal(item)}
                                  onArchive={handleArchiveDeal}
                                  onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                                />
                              </SortableCard>
                            ))}
                        </div>
                      );
                    })
                  )}
                </ColumnDroppable>
              </SortableContext>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Coming Soon")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Coming Soon</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredComingSoonDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Coming Soon"
                items={filteredComingSoonDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Coming Soon">
                  {filteredComingSoonDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No coming soon deals found</p>
                    </div>
                  ) : (
                    filteredComingSoonDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Active")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Active</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredActiveDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Active"
                items={filteredActiveDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Active">
                  {filteredActiveDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No active deals found</p>
                    </div>
                  ) : (
                    filteredActiveDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              {/* <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Active");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button> */}
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Under Contract")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Under Contract</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredUnderContractDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Under Contract"
                items={filteredUnderContractDeals.map((d) =>
                  String(d.createdat),
                )}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Under Contract">
                  {filteredUnderContractDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No under contract deals found</p>
                    </div>
                  ) : (
                    filteredUnderContractDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Under Contract");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Pending")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Pending</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredPendingDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Pending"
                items={filteredPendingDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Pending">
                  {filteredPendingDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No pending deals found</p>
                    </div>
                  ) : (
                    filteredPendingDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Pending");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Closed")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Closed</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredClosedDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Closed"
                items={filteredClosedDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Closed">
                  {filteredClosedDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No closed deals found</p>
                    </div>
                  ) : (
                    filteredClosedDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Closed");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Cancelled")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Cancelled</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredCancelledDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Cancelled"
                items={filteredCancelledDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Cancelled">
                  {filteredCancelledDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No cancelled deals found</p>
                    </div>
                  ) : (
                    filteredCancelledDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Cancelled");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button>
            </div>
            <div className="w-[86vw] max-w-[300px] sm:w-[270px] snap-start flex-shrink-0 flex flex-col bg-slate-50 rounded-lg">
              <div className="bg-transparent px-3 py-2 flex items-center justify-between flex-shrink-0">
                <div className="flex gap-x-4 items-center">
                  <div
                    className={`${getStatusColor("Expired")} rounded-full size-3`}
                  ></div>
                  <p className="font-medium">Expired</p>
                </div>
                <div className="bg-gray-100 rounded-full flex items-center justify-center size-6">
                  <p className="text-sm font-medium text-gray-500">
                    {filteredExpiryDeals.length}
                  </p>
                </div>
              </div>

              <SortableContext
                id="Expired"
                items={filteredExpiryDeals.map((d) => String(d.createdat))}
                strategy={verticalListSortingStrategy}
              >
                <ColumnDroppable id="Expired">
                  {filteredExpiryDeals.length === 0 ? (
                    <div className="text-center mt-5">
                      <p>No expired deals found</p>
                    </div>
                  ) : (
                    filteredExpiryDeals
                      .sort((a, b) => a.order - b.order)
                      .map((item, index) => (
                        <SortableCard
                          key={item.createdat}
                          item={item}
                          index={index}
                          onClick={() =>
                            navigate(`/dashboard/deals-detail/${item.createdat}?page=deal-board`)
                          }
                        >
                          <CommonCard
                            item={item}
                            users={users}
                            getInitials={getInitials}
                            getClientName={getClientName}
                            canManage={canManageDeal(item)}
                            canDelete={canManageDeal(item)}
                            onArchive={handleArchiveDeal}
                            onDelete={handleDeleteClick}
                            onMoveStage={moveDealToStage}
                          />
                        </SortableCard>
                      ))
                  )}
                </ColumnDroppable>
              </SortableContext>

              <button
                onClick={() => {
                  setIsOpen(true);
                  setIsStatus("Expired");
                }}
                className="mx-2 mb-2 py-1.5 text-xs text-slate-600 hover:bg-slate-200 transition flex justify-center gap-2 items-center rounded-md"
              >
                <Plus size={16} />
                Add Deal
              </button>
            </div>
          </div>
        </div>
        {/* DragOverlay floating card */}
        <DragOverlay>
          {activeCard ? (
            <div className="bg-white rounded-xl shadow-lg p-5 cursor-grabbing">
              <CommonCard
                item={activeCard}
                users={users}
                getInitials={getInitials}
                getClientName={getClientName}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      {isOpen && (
        <AddNewDeal
          isLoading={isLoading}
          onClose={() => setIsOpen(false)}
          isStatus={isStatus}
          onSubmit={(data, bool) => {
            submitdeal(data, bool);
          }}
          steps={steps}
        />
      )}
      {isEditOpen && (
        <AddNewDeal
          isLoading={isLoading}
          onClose={() => setIsEditOpen(false)}
          isedit={true}
          editdata={editdata}
          onSubmit={(data, bool, id) => {
            updatedeal(data, bool, id);
          }}
          steps={steps}
        />
      )}
      {deleteCandidate && (
        <DeleteDeal
          deal={deleteCandidate}
          onClose={() => setDeleteCandidate(null)}
          closepopup={() => setDeleteCandidate(null)}
          onSubmit={() => getalldeal()}
        />
      )}
    </div>
  );
}

const ColumnDroppable = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      id={id}
      className={`flex-1 min-h-0 overflow-y-auto flex flex-col gap-2 px-2 pb-2 transition-all panelScroll ${
        isOver ? "ring-2 ring-dashed ring-slate-400 bg-slate-100" : ""
      }`}
    >
      {children}
    </div>
  );
};

// const SortableCard = ({ item, index, children, onClick }) => {
//   const {
//     attributes,
//     listeners,
//     setNodeRef,
//     transform,
//     transition,
//     isDragging,
//   } = useSortable({ id: String(item.createdat), index });

//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     zIndex: isDragging ? 50 : "auto",
//     opacity: isDragging ? 0.9 : 1,
//   };

//   return (
//     <div
//       ref={setNodeRef}
//       style={style}
//       className="bg-white rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-1 transition  pt-2 pb-4 relative"
//     >
//       {/* ✅ Drag Handle Only */}
//       <div
//         {...attributes}
//         {...listeners}
//         className="absolute top-3 right-3 cursor-grab active:cursor-grabbing text-gray-400"
//       >
//         ⋮⋮
//       </div>

//       {/* ✅ Click Area */}
//       <div
//         onClick={(e) => {
//           e.stopPropagation();
//           if (!isDragging) {
//             onClick?.();
//           }
//         }}
//         className="cursor-pointer"
//       >
//         {children}
//       </div>
//     </div>
//   );
// };


// Stages a deal can be moved to from a card's tap-to-move menu. Mirrors the
// board's visible columns (excludes the legacy column-less stages Prospect /
// Listing Appointment).
const MOVE_STAGES = [
  "On Hold",
  "CMAs",
  "Pre-Market",
  "Coming Soon",
  "Active",
  "Under Contract",
  "Pending",
  "Closed",
  "Cancelled",
  "Expired",
];

const SortableCard = ({ item, index, children, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(item.createdat), index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : "auto",
    opacity: isDragging ? 0.9 : 1,
  };

  return (
<div
  ref={setNodeRef}
  style={style}
  {...attributes}
  {...listeners}
  onClick={() => {
    if (!isDragging) onClick?.();
  }}
  className="bg-white rounded-lg shadow-sm hover:shadow-md transition p-2 cursor-grab active:cursor-grabbing border border-slate-200"
>
  {children}
</div>
  );
};
// Exported for tests: this card decides on its own whose face it shows, and
// that decision has to be provable without standing up the whole board.
export const CommonCard = ({
  item,
  users,
  getInitials,
  getClientName,
  canManage = false,
  canDelete = false,
  onArchive,
  onDelete,
  onMoveStage,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const moveRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    if (!moveOpen) return;
    const handleClickOutside = (e) => {
      if (moveRef.current && !moveRef.current.contains(e.target)) {
        setMoveOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moveOpen]);

  const stopAndRun = (fn) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn?.();
  };

  // Whose face rides on the card. This used to be worked out inline from
  // splittype, using a strict === "TeamSplit" comparison: a stored "teamsplit"
  // or a stray trailing space put the wrong person's face on the card, and a
  // plain NoSplit deal showed the team lead while every report credited the
  // agent. creditedAgentId is the single rule the reports already use (and it
  // folds the split-type text before comparing), so the face on the card now
  // always matches the person the reports credit. Note this is only the FACE:
  // who can SEE the card is isOnDeal, which is deliberately wider.
  const creditedId = creditedAgentId(item);

  return (
    <div className="w-full relative">
      {canManage && (
        <div
          ref={menuRef}
          className="absolute top-1.5 right-1.5 z-20"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="size-6 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white text-slate-700"
            aria-label="Card actions"
          >
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 w-44 bg-white rounded-md shadow-lg border border-slate-200 py-1 text-sm">
              <button
                type="button"
                onClick={stopAndRun(() => onArchive?.(item))}
                className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50"
              >
                {item?.archived ? (
                  <>
                    <ArchiveRestore size={14} /> Restore
                  </>
                ) : (
                  <>
                    <Archive size={14} /> Archive
                  </>
                )}
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={stopAndRun(() => onDelete?.(item))}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-red-50 text-red-600"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <img
        src={
          item?.propertyphoto?.length > 0
            ? item?.propertyphoto[0]
            : "/assets/no-image.png"
        }
        onError={(e) => {
          e.currentTarget.src = "/assets/no-image.png";
        }}
        className="w-full h-[100px] object-cover rounded-md"
        alt="Property"
      />
      <h3 className="text-[13px] font-semibold mt-1.5 line-clamp-2 leading-snug">
        {item.propertyaddress}
      </h3>
      <div className="flex flex-row flex-wrap gap-1 mt-1.5">
        <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] rounded font-semibold text-linkColor">
          {item.dealtype}
        </span>
        {item.leadsource?.abbreviation && (
          <span className="bg-slate-100 px-1.5 py-0.5 text-[11px] rounded font-semibold text-linkColor">
            {item.leadsource.abbreviation}
          </span>
        )}
      </div>
      {(() => {
        // Resolved, " & "-joined client names (handles ID and legacy shapes,
        // already filtered of empties). "—" is the hook's not-yet-resolved
        // placeholder — skip it so the card doesn't flash a dash while the
        // contacts for ID-shape deals are still loading.
        const clientName = getClientName?.(item);
        if (!clientName || clientName === "—") return null;
        return (
          <p className="text-slate-500 text-xs mt-1.5">{clientName}</p>
        );
      })()}
      {item.offerPrice && (
        <p className="text-slate-900 text-sm font-bold mt-1">
          {formatCurrency(item.offerPrice)}
        </p>
      )}
      {item.listingAppointment && (
        <p className="text-[11px] font-semibold text-sky-600 mt-1.5">
          🎯 Target Date — {formatBoardDate(item.listingAppointment)}
        </p>
      )}
      <div className="h-px bg-slate-200 my-2"></div>
      {onMoveStage && (
        <div
          ref={moveRef}
          className="relative mb-2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMoveOpen((v) => !v);
            }}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
            aria-label="Move deal to another stage"
            title="Move to stage"
          >
            <span
              className={`${getStatusColor(item.dealStage)} size-2 rounded-full`}
            />
            {item.dealStage}
            <ChevronDown size={12} />
          </button>
          {moveOpen && (
            <div className="absolute left-0 bottom-full mb-1 z-30 w-44 bg-white rounded-md shadow-lg border border-slate-200 py-1 text-sm max-h-56 overflow-y-auto">
              <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-slate-400">
                Move to stage
              </p>
              {MOVE_STAGES.filter((s) => s !== item.dealStage).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMoveOpen(false);
                    onMoveStage(item, s);
                  }}
                  className="w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-slate-50"
                >
                  <span
                    className={`${getStatusColor(s)} size-2 rounded-full`}
                  />
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex gap-x-2 items-center">
        {users
          .filter(function (x) {
            return !!creditedId && String(x.id) === creditedId;
          })
          .map(function (y) {
            return (
              <div key={y.id} className="flex items-center gap-2 min-w-0">
                <img
                  src={y.profilepicture || userlogo}
                  alt=""
                  className="size-6 object-cover rounded-full flex-shrink-0"
                />
                <p className="text-slate-700 text-xs font-medium truncate">
                  {y.firstname + " " + y.lastname}
                </p>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default MainDealBoard;
